import { blockFromClick, type CreateOptions, type Span } from '@wiztime/core';
import { PointerKind } from '../../../lib/input';
import { minuteAtY, type LaneGeometry } from './laneHitTest';

/**
 * Pure rules of dragging a quick-mark tile onto the timeline (2026-10c §2): which pointers may
 * drag, when a press becomes a drag, which lane lies under the pointer and which block a drop
 * would create. Nothing here reads the DOM except the element list passed to `laneAtPoint`.
 */

/** A press that travels this many pixels becomes a drag; less is a normal click (the format brush). */
export const TILE_DRAG_THRESHOLD_PX = 5;

/** Attribute selector of a day lane (`BlockLane` marks every lane with its personal date). */
export const LANE_SELECTOR = '[data-testid="lane"][data-date]';

/** Mouse (primary button) and pen drag a tile; touch never does (long press and tap-tap remain). */
export function tileDragAllowed(pointerType: string, button: number): boolean {
  return (pointerType === PointerKind.Mouse && button === 0) || pointerType === PointerKind.Pen;
}

export function movedBeyondThreshold(startX: number, startY: number, x: number, y: number): boolean {
  return Math.hypot(x - startX, y - startY) >= TILE_DRAG_THRESHOLD_PX;
}

/** The elements under a viewport point, topmost first; empty where `elementsFromPoint` is unavailable. */
export function elementsAtPoint(x: number, y: number): Element[] {
  return typeof document.elementsFromPoint === 'function' ? document.elementsFromPoint(x, y) : [];
}

/** Elements that draw over the timeline without taking pointer input: the drop ghosts and the floating tip. */
const TRANSPARENT_SELECTOR = '[data-testid^="ghost-"], [data-testid="time-bubble"]';

/**
 * The lane under the pointer, from an `elementsFromPoint` list (topmost first). Only the topmost
 * element that takes pointer input counts: when it is not inside a lane (the project picker, the
 * overlap popover, the help overlay, a dialog, a menu lie above), there is no target even though
 * a lane sits below.
 */
export function laneAtPoint(elements: readonly Element[]): HTMLElement | null {
  const top = elements.find((element) => !element.matches(TRANSPARENT_SELECTOR));
  if (top === undefined) return null;
  const lane = top.closest(LANE_SELECTOR);
  return lane instanceof HTMLElement ? lane : null;
}

/**
 * The block a drop at viewport `clientY` creates: the same creation rules as a click on the
 * lane (`blockFromClick`: snapped-down start, held inside the free gap, an overlay when dropped
 * on a block, cut at the day end) with the project's quick duration as the block length. Null
 * when the pointer lies outside the personal day.
 */
export function tileDropSpan(
  geometry: LaneGeometry,
  clientY: number,
  options: CreateOptions,
  obstacles: readonly Span[],
  durationMin: number,
): Span | null {
  return blockFromClick(minuteAtY(geometry, clientY), { ...options, defaultDurationMin: durationMin }, obstacles);
}
